import { Slot } from '@radix-ui/react-slot';
import { Link } from '@tanstack/react-router';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import type { IconName } from 'lucide-react/dynamic';
import * as React from 'react';
import { CONFIG } from '@/config-globals';
import { cn } from '@/lib/utils';
import { Icon } from './icon';

function Breadcrumb({ ...props }: React.ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        'text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm break-words sm:gap-2.5',
        className,
      )}
      {...props}
    />
  );
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn('inline-flex items-center gap-1.5', className)}
      {...props}
    />
  );
}

function BreadcrumbLink({
  asChild,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean;
}) {
  const Comp = asChild ? Slot : Link;

  return (
    <Comp
      data-slot="breadcrumb-link"
      className={cn('hover:text-foreground transition-colors', className)}
      to={props.href as string}
      {...props}
    />
  );
}

function BreadcrumbPage({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn('text-foreground font-normal', className)}
      {...props}
    />
  );
}

function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn('[&>svg]:size-3.5', className)}
      {...props}
    >
      {children ?? <ChevronRight />}
    </li>
  );
}

function BreadcrumbEllipsis({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn('flex size-9 items-center justify-center', className)}
      {...props}
    >
      <MoreHorizontal className="size-4" />
      <span className="sr-only">More</span>
    </span>
  );
}

interface BreadcrumbItem {
  label: string;
  route?: string;
  active?: boolean;
  onClick?: () => void;
}

export type BreadcrumbsProps = {
  title?: string | React.ReactNode;
  icon?: IconName;
  action?: any;
  /** Botão voltar à esquerda do título (ex.: console do check-in → escolha da operação). */
  onBack?: () => void;
  items: BreadcrumbItem[];
};

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ title, icon, action, items }) => {
  return (
    <nav className="flex flex-wrap w-full">
      <div className="mb-3 flex flex-wrap flex-row items-center content-center w-full">
        <Breadcrumb>
          <BreadcrumbList className="eyebrow flex-nowrap gap-1.5 text-muted-foreground">
            {items.length ? (
              <>
                <BreadcrumbItem key="home">
                  <BreadcrumbLink href="/">
                    <Icon name="home" className="size-3.5" />
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {items.map((item, index) => (
                  <React.Fragment key={index}>
                    <BreadcrumbSeparator />
                    {item.active ? (
                      <BreadcrumbItem>
                        <BreadcrumbPage className="eyebrow text-foreground-2">
                          {item.label}
                        </BreadcrumbPage>
                      </BreadcrumbItem>
                    ) : (
                      <BreadcrumbItem>
                        <BreadcrumbLink
                          href={item.route ? `/${item.route}` : '#'}
                          onClick={item?.onClick ?? undefined}
                        >
                          {item.label}
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                    )}
                  </React.Fragment>
                ))}
              </>
            ) : null}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className="flex flex-wrap mb-6 w-full items-center content-center justify-between gap-3">
        <span className="display-title flex flex-wrap items-center content-center text-[26px] md:text-[32px] text-foreground">
          {icon && <Icon name={icon} className="w-6 h-6 mr-2 text-olive-icon" />}
          {title}
        </span>
        {action}
      </div>
    </nav>
  );
};

const toHref = (route: string) => `/${route.replace(/^\/+/, '')}`;

/** Trilha curta do topo: ícone home + etapas anteriores clicáveis + etapa atual. */
export const BreadcrumbTrail: React.FC<Pick<BreadcrumbsProps, 'items'>> = ({ items }) => {
  return (
    <Breadcrumb>
      <BreadcrumbList className="col-label flex-nowrap gap-2 overflow-hidden whitespace-nowrap text-[10.5px] text-faint sm:gap-2">
        <BreadcrumbItem key="home">
          <BreadcrumbLink
            href="/"
            aria-label={CONFIG.APP_NAME}
            className="hover:text-sand"
          >
            <Icon name="house" className="size-3.5" />
          </BreadcrumbLink>
        </BreadcrumbItem>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              {item.active ? (
                <BreadcrumbPage className="font-bold text-muted-foreground">
                  {item.label}
                </BreadcrumbPage>
              ) : item.route ? (
                <BreadcrumbLink
                  href={toHref(item.route)}
                  onClick={item.onClick}
                  className="hover:text-sand"
                >
                  {item.label}
                </BreadcrumbLink>
              ) : item.onClick ? (
                <button
                  type="button"
                  onClick={item.onClick}
                  className="col-label cursor-pointer text-[10.5px] transition-colors hover:text-sand"
                >
                  {item.label}
                </button>
              ) : (
                <span>{item.label}</span>
              )}
            </BreadcrumbItem>
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Breadcrumbs,
};
